import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

// Extend dayjs with the relativeTime plugin to support .fromNow()
dayjs.extend(relativeTime)

/**
 * Result of the timeDisplay function
 */
export interface TimeDisplayResult {
  /**
   * Human-readable time display
   */
  display: string
  /**
   * Flag indicating if the time should be shown as a warning (too old)
   */
  isWarning: boolean
}

/**
 * Format a timestamp into a human-readable relative time string
 *
 * @param value - The timestamp to format
 * @param warningThresholdHours - Hours after which to flag as warning (default: 36)
 * @returns An object with the formatted display string and a warning flag
 */
export const timeDisplay = (value: string | null, warningThresholdHours = 36): TimeDisplayResult => {
  // Handle null/undefined values
  if (!value) return { display: '-', isWarning: false }

  const now = dayjs()
  const valueTime = dayjs(value)

  // Calculate time differences for comparison
  const timeDifferenceHours = now.diff(valueTime, 'hour')
  const isInFuture = valueTime.isAfter(now)

  // Format the relative time string
  let differenceDisplay = valueTime.fromNow()

  // For future times, we need to ensure consistent formatting with "In X"
  if (isInFuture) {
    differenceDisplay = `In ${differenceDisplay.replace('in ', '')}`
  }

  // Flag as warning if more than the threshold hours in the past
  const isWarning = !isInFuture && timeDifferenceHours > warningThresholdHours

  return { display: differenceDisplay, isWarning }
}
